import React, { useCallback, useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Menu,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { ORGANIZATIONS } from "src/platform/constants/routes";
import { StorageKeys } from "src/platform/constants/Storage";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";
import { useNotification } from "src/shell/contexts/notificationContext";

import OrganizationListSkeleton from "../components/OrganizationListSkeleton";
import { Organization } from "../types/OrganizationTypes";

const OrganizationList = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { showNotification } = useNotification();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [selectingOrg, setSelectingOrg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuOrg, setMenuOrg] = useState<Organization | null>(null);
  const [leaveOrg, setLeaveOrg] = useState<Organization | null>(null);

  const { call: fetchOrganizations } = useRegistryApi<Organization[]>(
    "organization",
    "listOrganizations",
  );

  const loadOrganizations = useCallback(
    async (isRetry = false) => {
      if (isRetry) {
        setRetrying(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const orgs = await fetchOrganizations();
        setOrganizations(orgs);
      } catch {
        try {
          const recentOrgsStr = localStorage.getItem(
            StorageKeys.RECENT_ORGANIZATIONS,
          );
          if (recentOrgsStr) {
            setOrganizations(JSON.parse(recentOrgsStr));
          }
        } catch {
          // ignore parse errors
        }
        setError("Failed to load organizations. Please try again later.");
      } finally {
        setLoading(false);
        setRetrying(false);
      }
    },
    [fetchOrganizations],
  );

  useEffect(() => {
    void loadOrganizations();
  }, [loadOrganizations]);

  const handleSelectOrganization = async (organization: Organization) => {
    setSelectingOrg(organization.alias);
    try {
      await orgSessionManager.selectOrg(organization.alias);
      showNotification(`Opened ${organization.name} organization.`, "success");
      window.location.href = ORGANIZATIONS.DETAILS.getPath(organization.alias);
    } catch {
      setError("Failed to select organization. Please try again.");
      setSelectingOrg(null);
    }
  };

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    org: Organization,
  ) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuOrg(org);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOrg(null);
  };

  if (loading) {
    return <OrganizationListSkeleton />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "24px",
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            color: "text.primary",
            margin: 0,
          }}
        >
          Organizations
        </Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to={ORGANIZATIONS.CREATE.path}
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            borderRadius: "8px",
            padding: "8px 14px",
            textTransform: "none",
            border: `1px solid ${theme.palette.primary.main}`,
          }}
        >
          + Create organization
        </Button>
      </Box>

      {/* Error banner with retry (C9: escape route) */}
      {error && (
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "12px 16px",
            marginBottom: "20px",
            borderRadius: "8px",
            backgroundColor: alpha(theme.palette.error.main, 0.12),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: theme.palette.error.main,
            fontSize: "13px",
          }}
        >
          <span>{error}</span>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void loadOrganizations(true)}
            disabled={retrying}
            startIcon={
              retrying ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              flexShrink: 0,
              fontSize: "12px",
              fontWeight: 600,
              textTransform: "none",
              borderColor: alpha(theme.palette.error.main, 0.4),
              borderRadius: "6px",
              color: theme.palette.error.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                borderColor: alpha(theme.palette.error.main, 0.4),
              },
            }}
          >
            {retrying ? "Retrying..." : "Try again"}
          </Button>
        </Box>
      )}

      {/* Empty state with illustration (C10: anatomy — illustration → title → body → CTA → link) */}
      {organizations.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            padding: "48px 24px",
            color: "text.secondary",
            border: `1px dashed ${alpha(theme.palette.divider, 0.12)}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* Hub-spoke SVG illustration — org node with satellite project nodes */}
          <Box
            role="presentation"
            aria-hidden="true"
            sx={{ marginBottom: "16px", opacity: 0.9 }}
          >
            <SvgIcon
              sx={{ width: 180, height: 160, color: "primary.main" }}
              viewBox="0 0 180 160"
            >
              <circle
                cx="90"
                cy="40"
                r="28"
                fill={alpha(theme.palette.primary.main, 0.12)}
              />
              <circle
                cx="90"
                cy="40"
                r="12"
                fill={alpha(theme.palette.primary.main, 0.9)}
              />
              <circle
                cx="40"
                cy="80"
                r="10"
                fill={alpha(theme.palette.primary.main, 0.06)}
                stroke={alpha(theme.palette.primary.main, 0.5)}
                strokeWidth="2"
              />
              <circle
                cx="140"
                cy="80"
                r="10"
                fill={alpha(theme.palette.primary.main, 0.06)}
                stroke={alpha(theme.palette.primary.main, 0.5)}
                strokeWidth="2"
              />
              <circle
                cx="60"
                cy="120"
                r="10"
                fill={alpha(theme.palette.primary.main, 0.06)}
                stroke={alpha(theme.palette.primary.main, 0.5)}
                strokeWidth="2"
              />
              <circle
                cx="120"
                cy="120"
                r="10"
                fill={alpha(theme.palette.primary.main, 0.06)}
                stroke={alpha(theme.palette.primary.main, 0.5)}
                strokeWidth="2"
              />
              <line
                x1="90"
                y1="52"
                x2="40"
                y2="74"
                stroke={alpha(theme.palette.primary.main, 0.4)}
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <line
                x1="90"
                y1="52"
                x2="140"
                y2="74"
                stroke={alpha(theme.palette.primary.main, 0.4)}
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <line
                x1="90"
                y1="52"
                x2="60"
                y2="114"
                stroke={alpha(theme.palette.primary.main, 0.4)}
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <line
                x1="90"
                y1="52"
                x2="120"
                y2="114"
                stroke={alpha(theme.palette.primary.main, 0.4)}
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            </SvgIcon>
          </Box>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "text.primary",
              marginBottom: "8px",
            }}
          >
            No organizations yet
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              maxWidth: 420,
              lineHeight: 1.6,
              marginBottom: "16px",
            }}
          >
            Organizations group your projects, stacks, and deployments. Create
            your first organization to get started.
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to={ORGANIZATIONS.CREATE.path}
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Create organization
          </Button>
          <MuiLink
            href="#"
            onClick={(e) => e.preventDefault()}
            sx={{
              marginTop: "12px",
              fontSize: "13px",
              fontWeight: 500,
              color: "text.disabled",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            Learn about organizations →
          </MuiLink>
        </Box>
      ) : (
        /* Table in a card container */
        <Box
          sx={{
            backgroundColor: "background.paper",
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
            padding: "20px",
            overflow: "auto",
          }}
        >
          <TableContainer>
            <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
              <TableHead>
                <TableRow>
                  {["Name", "Alias", "Role", "Projects", "Status", ""].map(
                    (col) => (
                      <TableCell
                        key={col}
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: "text.secondary",
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => {
                  const isSelecting = selectingOrg === org.alias;
                  return (
                    <TableRow
                      key={org.alias}
                      hover
                      onClick={() =>
                        !isSelecting && handleSelectOrganization(org)
                      }
                      sx={{
                        cursor: isSelecting ? "wait" : "pointer",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.03,
                          ),
                        },
                        "& td": {
                          padding: "12px",
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                          fontSize: "13px",
                          color: "text.primary",
                        },
                      }}
                    >
                      {/* Name — blue link */}
                      <TableCell>
                        <MuiLink
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleSelectOrganization(org);
                          }}
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            fontSize: "13px",
                            textDecoration: "none",
                            cursor: "pointer",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {org.name}
                        </MuiLink>
                      </TableCell>

                      {/* Alias — monospace */}
                      <TableCell>
                        <Box
                          component="code"
                          sx={{
                            fontFamily: "monospace",
                            color: "primary.main",
                            fontSize: "13px",
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08,
                            ),
                            padding: "2px 5px",
                            borderRadius: "4px",
                          }}
                        >
                          {org.alias}
                        </Box>
                      </TableCell>

                      {/* Role — chip */}
                      <TableCell>
                        <Chip
                          label="Admin"
                          size="small"
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            height: "auto",
                            padding: "3px 10px",
                            borderRadius: "12px",
                            backgroundColor: alpha(
                              theme.palette.text.secondary,
                              0.12,
                            ),
                            color: "text.secondary",
                            border: `1px solid ${alpha(theme.palette.text.secondary, 0.15)}`,
                            "& .MuiChip-label": { padding: 0 },
                          }}
                        />
                      </TableCell>

                      {/* Projects count */}
                      <TableCell>
                        <Typography sx={{ fontSize: "13px" }}>N/A</Typography>
                      </TableCell>

                      {/* Status — chip */}
                      <TableCell>
                        <Chip
                          label={org.status || "Active"}
                          size="small"
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            height: "auto",
                            padding: "3px 10px",
                            borderRadius: "12px",
                            backgroundColor:
                              (org.status || "Active") === "Active"
                                ? alpha(theme.palette.success.main, 0.12)
                                : alpha(theme.palette.warning.main, 0.12),
                            color:
                              (org.status || "Active") === "Active"
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                            border:
                              (org.status || "Active") === "Active"
                                ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                : `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            "& .MuiChip-label": { padding: 0 },
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                            opacity: 0,
                            transition: "opacity 0.15s",
                            "tr:hover &": { opacity: 1 },
                          }}
                        >
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleSelectOrganization(org);
                            }}
                            disabled={isSelecting}
                            startIcon={
                              isSelecting ? (
                                <CircularProgress size={12} color="inherit" />
                              ) : undefined
                            }
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "primary.main",
                              textTransform: "none",
                              minWidth: "auto",
                              padding: "5px 10px",
                            }}
                          >
                            {isSelecting ? "Opening..." : "View"}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, org)}
                            aria-label={`More actions for ${org.name}`}
                            title="More actions"
                            sx={{ color: "text.secondary" }}
                          >
                            <MoreHorizIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* More dropdown menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            minWidth: "180px",
            boxShadow: theme.shadows[4],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "8px",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuOrg) appNavigation.goToOrganizationSettings(menuOrg.alias);
            handleMenuClose();
          }}
        >
          Manage members
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Billing</MenuItem>
        <MenuItem
          onClick={() => {
            if (menuOrg) setLeaveOrg(menuOrg);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          Leave organization
        </MenuItem>
      </Menu>

      {/* Leave organization confirmation modal (C3/C5: destructive action confirmation) */}
      <Dialog
        open={Boolean(leaveOrg)}
        onClose={() => setLeaveOrg(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "16px", fontWeight: 700 }}>
          Leave organization?
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontSize: "14px", color: "text.secondary", lineHeight: 1.6 }}
          >
            You are about to leave{" "}
            <Box
              component="strong"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {leaveOrg?.name}
            </Box>
            . You will lose access to all its projects, stacks, and resources.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px 20px", gap: "8px" }}>
          <Button
            onClick={() => setLeaveOrg(null)}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setLeaveOrg(null)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                borderColor: "error.main",
              },
            }}
          >
            Leave organization
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationList;
